import { useEffect, useMemo, useState } from 'react';
import 'katex/dist/katex.min.css';

/**
 * Renders markdown with optional math/KaTeX support.
 *
 * NOTE: We lazy-load react-markdown/remark-math/rehype-katex at runtime
 * to avoid CRA/Jest failing on ESM-only dependencies in node_modules.
 */
function MarkdownRenderer({ content, inline = false }) {
  const [renderer, setRenderer] = useState(null);

  const text = useMemo(() => {
    if (content === null || content === undefined) return '';
    return String(content);
  }, [content]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [{ default: ReactMarkdown }, remarkMathMod, rehypeKatexMod] =
          await Promise.all([
            import('react-markdown'),
            import('remark-math'),
            import('rehype-katex'),
          ]);

        if (cancelled) return;

        const remarkMath = remarkMathMod.default || remarkMathMod;
        const rehypeKatex = rehypeKatexMod.default || rehypeKatexMod;

        setRenderer(() => ({ ReactMarkdown, remarkMath, rehypeKatex }));
      } catch (e) {
        // Fall back to plain text if markdown libs fail to load.
        if (!cancelled) setRenderer(null);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fallback while loading or if libs aren’t available
  if (!renderer) {
    if (inline) return <span>{text}</span>;
    return <pre style={{ whiteSpace: 'pre-wrap' }}>{text}</pre>;
  }

  const { ReactMarkdown, remarkMath, rehypeKatex } = renderer;

  return (
    <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
      {text}
    </ReactMarkdown>
  );
}

export default MarkdownRenderer;